import { Injectable, computed, inject, signal } from '@angular/core';
import {
  HD_BODYGRAPH_CHANNELS,
  findHdBodygraphChannelByGates,
} from '../hd-bodygraph-metadata';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import {
  MEMORY_HINT_LETTERS_PER_REQUEST,
  fuzzyMemoryMatch,
  matchesBySimilarity,
  matchesByWordOverlap,
  normalizeMemoryInput,
  revealNextChar,
} from '../_hd-memory-text-matching';

/**
 * Spelvariant. `numbers` toont per vraag het kanaal-nummer als `min - max`,
 * `keynotes` toont per vraag de kanaal-keynote, `mixed` kiest random per
 * vraag tussen `numbers`-gedrag of `keynotes`-gedrag (50/50). `memory`
 * highlight het kanaal op de chart en vraagt de speler om naam en korte
 * omschrijving in te typen — het invoer-gebeuren ligt buiten de twee-klik
 * flow en gebruikt `submitMemoryAnswer` i.p.v. `selectGate`.
 */
export type HdChannelGuessingMode = 'numbers' | 'keynotes' | 'mixed' | 'memory';

/**
 * Concrete vraagweergave per kanaal: enkel `numbers` of `keynotes`. De
 * `mixed`-modus wordt bij het starten van het spel deterministisch
 * uitgerold naar één van deze twee per kanaal. `memory` valt buiten dit
 * mechanisme: de per-kanaal-map blijft leeg en het paneel raadpleegt de
 * spelmodus rechtstreeks.
 */
export type HdChannelGuessingQuestionMode = 'numbers' | 'keynotes';

/**
 * Resultaat van één klik op een poort tijdens Kanalenjacht.
 *
 * - `first-selected`: dit was de eerste klik voor de huidige vraag.
 * - `first-deselected`: tweede klik op dezelfde poort heeft de selectie geannuleerd.
 * - `correct`: tweede klik vormt samen met de eerste het gevraagde kanaal.
 * - `wrong`: tweede klik vormt een ander kanaal of geen kanaal.
 * - `ignored`: het spel staat niet open voor input (geen actief spel of geen huidig kanaal).
 */
export type HdChannelSelectionResult =
  | 'first-selected'
  | 'first-deselected'
  | 'correct'
  | 'wrong'
  | 'ignored';

export type HdChannelGuessingHistoryEvent =
  | { readonly kind: 'correct'; readonly channelKey: string; readonly points: number }
  | { readonly kind: 'wrong'; readonly channelKey: string; readonly points: number }
  | { readonly kind: 'hint'; readonly channelKey: string; readonly points: number }
  | { readonly kind: 'skip'; readonly channelKey: string; readonly points: number }
  | { readonly kind: 'streak-bonus'; readonly streak: number; readonly points: number }
  | { readonly kind: 'perfect-bonus'; readonly points: number };

export type HdChannelGuessingScoreLevel = 1 | 2 | 3 | 4 | 5;

/**
 * Resultaat van één `submitMemoryAnswer`-aanroep. `correct` betekent dat
 * zowel naam als omschrijving overeenkwamen; `wrong` markeert per veld of
 * de fout daar zat zodat het paneel de juiste invoer rood kan kleuren.
 * `ignored` wanneer er geen actief memory-spel loopt of geen huidig kanaal.
 */
export type HdChannelGuessingMemoryAnswerResult =
  | { readonly kind: 'correct' }
  | { readonly kind: 'wrong'; readonly nameWrong: boolean; readonly descriptionWrong: boolean }
  | { readonly kind: 'ignored' };

/** Twee invoervelden in de memory-modus: naam van het kanaal of designomschrijving. */
export type HdChannelGuessingMemoryField = 'name' | 'description';

/**
 * Resultaat van één `validateMemoryField`-aanroep. `correct` levert tevens de
 * canonieke (juist gespelde) waarde mee zodat het paneel het invoerveld kan
 * vervangen door de officiële spelling — handig bij fuzzy-match-acceptaties.
 * `wrong` houdt de huidige waarde aan (geen autocorrect bij fout antwoord).
 * `ignored` wanneer validatie nu niet mag (geen actief memory-spel, of het
 * andere veld werd reeds gevalideerd waardoor enkel `submit` nog rest).
 */
export type HdChannelGuessingMemoryValidationResult =
  | { readonly kind: 'correct'; readonly canonical: string }
  | { readonly kind: 'wrong' }
  | { readonly kind: 'ignored' };

/**
 * Stand-bevriezing van welke memory-velden voor het huidige kanaal reeds
 * succesvol gevalideerd werden. Een `true`-vlag impliceert dat het veld
 * bevroren is op zijn canonieke waarde en niet meer bewerkbaar is.
 */
export interface HdChannelGuessingMemoryValidations {
  readonly name: boolean;
  readonly description: boolean;
}

/**
 * Nieuwe waarden voor de twee memory-invoervelden na een hint-verzoek. Het
 * paneel past deze rechtstreeks toe op zijn invoer-signals zonder verdere
 * interpretatie. Velden die niet in aanmerking kwamen voor onthulling
 * krijgen hun bestaande waarde terug.
 */
export interface HdChannelGuessingMemoryHintReveal {
  readonly name: string;
  readonly description: string;
}

/**
 * Per-kanaal progressie die bewaard blijft als een kanaal wordt overgeslagen
 * en later terugkeert. Bevat foute pogingen plus aantal gevraagde hints.
 */
interface HdChannelGuessingChannelProgress {
  readonly wrongAttempts: number;
  readonly hintsUsed: number;
}

const STREAK_BONUS_INTERVAL = 10;
const STREAK_BONUS_POINTS = 2;
const PERFECT_GAME_BONUS = 5;
const HINT_COST = 0.5;
const SKIP_COST = 0.5;
const CORRECT_POINTS = 1;
const WRONG_POINTS = -1;

/** Vaste hint-capaciteit voor Kanalenjacht: 1 = hoofdcircuit, 2 = verbonden centers. */
const MAX_HINTS_PER_CHANNEL = 2;

const TOTAL_CHANNELS = HD_BODYGRAPH_CHANNELS.length;
const STREAK_BONUS_OPPORTUNITIES = Math.floor(TOTAL_CHANNELS / STREAK_BONUS_INTERVAL);
const MAX_POSSIBLE_SCORE =
  TOTAL_CHANNELS * CORRECT_POINTS +
  STREAK_BONUS_OPPORTUNITIES * STREAK_BONUS_POINTS +
  PERFECT_GAME_BONUS;

/**
 * Service voor de Kanalenjacht-spelmodus. Houdt de volledige spel-state
 * (wachtrij, score, streak, hints, selectie-buffer, geraden kanalen) bij
 * via Angular signals. Bewust géén gedeelde abstractie met
 * `HdGateGuessingGameService`: de twee-klik flow en evaluatie tegen
 * kanaal-set verschillen voldoende dat een gemeenschappelijke base meer
 * kost dan oplevert (zie `hd-kanaal-leren-spel.md`, sectie *Afweging*).
 *
 * Elk kanaal wordt onafhankelijk gevraagd: poorten die al onthuld zijn door
 * een eerder correct geraden kanaal blijven klikbaar voor de volgende vraag
 * (de speler herselecteert ze om het volgende kanaal te vormen).
 *
 * Hint-tekst-compositie zit in het paneel (zoals bij Poortenjacht): de
 * service exposeert enkel het aantal gevraagde hints; het paneel formatteert
 * via `HdGateInfoService`.
 */
@Injectable({ providedIn: 'root' })
export class HdChannelGuessingGameService {
  static readonly maxPossibleScore = MAX_POSSIBLE_SCORE;
  static readonly maxHintsPerChannel = MAX_HINTS_PER_CHANNEL;
  static readonly totalChannels = TOTAL_CHANNELS;

  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /**
   * Snapshot van de inhoudstaal op het moment van `start()`. Bevroren tijdens
   * een actief spel: een mid-game wissel van de globale taal-override mag de
   * lopende keynote/naam-validaties niet onder de speler vandaan trekken.
   */
  private readonly _currentLang = signal<string>(this.contentLang.effectiveLang());

  private readonly _isActive = signal(false);
  private readonly _mode = signal<HdChannelGuessingMode>('numbers');
  private readonly _perChannelQuestionMode =
    signal<ReadonlyMap<string, HdChannelGuessingQuestionMode>>(new Map());
  private readonly _queue = signal<readonly string[]>([]);
  private readonly _events = signal<readonly HdChannelGuessingHistoryEvent[]>([]);
  private readonly _score = signal(0);
  private readonly _streak = signal(0);
  private readonly _selectedGate = signal<number | null>(null);
  private readonly _wrongAttemptsForCurrentChannel = signal(0);
  private readonly _hintsUsedForCurrentChannel = signal(0);
  private readonly _hintsUsedTotal = signal(0);
  private readonly _wrongAttemptsTotal = signal(0);
  private readonly _skipsTotal = signal(0);
  private readonly _correctChannelsTotal = signal(0);
  private readonly _channelProgress = new Map<string, HdChannelGuessingChannelProgress>();

  /**
   * Per-veld validatiestatus voor het huidige memory-kanaal. Eens één veld
   * `true` staat, kan het andere veld niet meer afzonderlijk gevalideerd
   * worden (de speler moet dan submitten). Wordt gereset bij elke
   * kanaal-wissel via `loadChannelProgress`.
   */
  private readonly _currentMemoryValidations =
    signal<HdChannelGuessingMemoryValidations>({ name: false, description: false });

  readonly isActive = this._isActive.asReadonly();
  readonly mode = this._mode.asReadonly();

  /**
   * Inhoudstaal die de huidige game gebruikt voor channel-translations.
   * Bevroren tijdens een actief spel; in idle-state volgt het de live override.
   */
  readonly currentLang = this._currentLang.asReadonly();
  readonly events = this._events.asReadonly();
  readonly score = this._score.asReadonly();
  readonly streak = this._streak.asReadonly();
  readonly selectedGate = this._selectedGate.asReadonly();
  readonly wrongAttemptsForCurrentChannel = this._wrongAttemptsForCurrentChannel.asReadonly();
  readonly hintsUsedForCurrentChannel = this._hintsUsedForCurrentChannel.asReadonly();
  readonly maxPossibleScore = MAX_POSSIBLE_SCORE;
  readonly maxHintsPerChannel = MAX_HINTS_PER_CHANNEL;
  readonly totalChannels = TOTAL_CHANNELS;

  readonly currentChannelKey = computed<string | null>(() => {
    const queue = this._queue();
    return queue.length > 0 ? queue[0] : null;
  });

  readonly currentChannelGates = computed<readonly [number, number] | null>(() => {
    const key = this.currentChannelKey();
    if (key == null) {
      return null;
    }
    const channel = HD_BODYGRAPH_CHANNELS.find(c => c.key === key);
    return channel ? channel.gates : null;
  });

  readonly revealedChannels = computed<ReadonlySet<string>>(
    () => new Set(
      this._events()
        .filter((e): e is Extract<HdChannelGuessingHistoryEvent, { kind: 'correct' }> => e.kind === 'correct')
        .map(e => e.channelKey),
    ),
  );

  /**
   * Poorten die door een correct geraden kanaal permanent zichtbaar blijven
   * op de chart (defined-rendering). Worden niet gebruikt om klikken te
   * blokkeren: de speler mag een al-onthulde poort opnieuw aanklikken om
   * deze als selectie voor een volgend kanaal te gebruiken.
   */
  readonly revealedGates = computed<ReadonlySet<number>>(() => {
    const set = new Set<number>();
    for (const channelKey of this.revealedChannels()) {
      const channel = HD_BODYGRAPH_CHANNELS.find(c => c.key === channelKey);
      if (channel) {
        set.add(channel.gates[0]);
        set.add(channel.gates[1]);
      }
    }
    return set;
  });

  readonly isFinished = computed(
    () => this._isActive() && this._queue().length === 0,
  );

  readonly isPerfectGame = computed(
    () =>
      this.isFinished() &&
      this._wrongAttemptsTotal() === 0 &&
      this._hintsUsedTotal() === 0 &&
      this._skipsTotal() === 0,
  );

  /**
   * Of er nog een hint kan opgevraagd worden voor het huidige kanaal. In
   * `numbers`/`keynotes`/`mixed`-modus geldt de vaste cap van
   * `MAX_HINTS_PER_CHANNEL`. In `memory`-modus is er geen cap (er kunnen
   * meerdere letters onthuld moeten worden), maar een hint heeft geen zin
   * meer wanneer beide invoervelden reeds gevalideerd zijn.
   */
  readonly canRequestHint = computed(() => {
    if (!this._isActive() || this.currentChannelKey() == null) {
      return false;
    }
    if (this._mode() === 'memory') {
      const v = this._currentMemoryValidations();
      return !(v.name && v.description);
    }
    return this._hintsUsedForCurrentChannel() < MAX_HINTS_PER_CHANNEL;
  });

  readonly canSkip = computed(
    () => this._isActive() && this.currentChannelKey() != null && this._queue().length > 1,
  );

  /** Vlag voor het paneel: in memory-modus toont de UI tekstvelden i.p.v. de twee-klik flow. */
  readonly isMemoryMode = computed(() => this._mode() === 'memory');

  /** Validatiestatus van de twee memory-invoervelden voor het huidige kanaal. */
  readonly currentMemoryValidations = this._currentMemoryValidations.asReadonly();

  /**
   * Of het paneel nu een validatie-knop voor `field` mag tonen. De validatie
   * is enkel beschikbaar zolang nog géén van beide velden succesvol werd
   * gevalideerd: zodra dat gebeurt verdwijnen beide validatie-knoppen en
   * moet de speler `submitMemoryAnswer` gebruiken voor de finale evaluatie.
   * Het `field`-argument is voorzien voor toekomstige asymmetrie maar geeft
   * vandaag voor beide velden hetzelfde antwoord.
   */
  canValidateMemoryField(_field: HdChannelGuessingMemoryField): boolean {
    if (!this._isActive() || this._mode() !== 'memory') {
      return false;
    }
    if (this.currentChannelKey() == null) {
      return false;
    }
    const v = this._currentMemoryValidations();
    return !v.name && !v.description;
  }

  /**
   * Vraagweergave voor het huidige kanaal. Bij `mixed`-modus is dit de bij
   * spel-start gekozen waarde voor dat specifieke kanaal; bij `numbers` of
   * `keynotes` modus is dit dezelfde waarde voor alle kanalen. In `memory`-modus
   * is er geen klassieke vraagweergave en geeft dit `null` terug.
   */
  readonly currentQuestionMode = computed<HdChannelGuessingQuestionMode | null>(() => {
    const key = this.currentChannelKey();
    if (key == null) {
      return null;
    }
    return this._perChannelQuestionMode().get(key) ?? null;
  });

  readonly scoreLevel = computed<HdChannelGuessingScoreLevel | null>(() =>
    this.isFinished() ? this.computeScoreLevel(this._score()) : null,
  );

  /**
   * Geeft de bij spel-start vastgelegde vraagweergave voor een specifiek
   * kanaal terug. Wordt gebruikt door de history-weergave om correct te
   * tonen wat de speler te zien kreeg (kanaal-nummer of keynote), zodat de
   * answer niet verraden wordt op fout/skip/hint-rijen in `keynotes`- en
   * `mixed`-modus. Geeft `null` buiten een actief spel.
   */
  questionModeForChannel(channelKey: string): HdChannelGuessingQuestionMode | null {
    return this._perChannelQuestionMode().get(channelKey) ?? null;
  }

  start(
    mode: HdChannelGuessingMode = 'numbers',
    shuffleFn: (keys: readonly string[]) => readonly string[] = shuffleChannelKeysFisherYates,
    randomFn: () => number = Math.random,
  ): void {
    this.resetState();
    const allKeys = HD_BODYGRAPH_CHANNELS.map(c => c.key);
    const queue = shuffleFn(allKeys);
    this._mode.set(mode);
    this._currentLang.set(this.contentLang.effectiveLang());
    this._perChannelQuestionMode.set(buildPerChannelQuestionMode(queue, mode, randomFn));
    this._queue.set(queue);
    this._isActive.set(true);
    this.loadChannelProgress(this.currentChannelKey());
  }

  stop(): void {
    this.resetState();
  }

  restart(): void {
    this.start(this._mode());
  }

  selectGate(gate: number): HdChannelSelectionResult {
    if (!this._isActive() || this._mode() === 'memory') {
      return 'ignored';
    }

    const currentKey = this.currentChannelKey();
    if (currentKey == null) {
      return 'ignored';
    }

    const selected = this._selectedGate();
    if (selected == null) {
      this._selectedGate.set(gate);
      return 'first-selected';
    }
    if (selected === gate) {
      this._selectedGate.set(null);
      return 'first-deselected';
    }

    return this.evaluateSecondSelection(currentKey, selected, gate);
  }

  /**
   * Verwerkt een memory-modus antwoord (kanaalnaam + korte omschrijving). Beide
   * inputs worden case-insensitief, zonder diakritische tekens en met een
   * fuzzy-tolerantie (Levenshtein, zie `MEMORY_FUZZY_*`) vergeleken zodat
   * lichte typfouten geaccepteerd worden. Een veld dat reeds via
   * `validateMemoryField` werd bevroren wordt automatisch als correct
   * beschouwd. Bij correct antwoord verloopt scoring/streak/queue identiek
   * aan de gewone twee-klik flow; bij fout antwoord wordt per veld
   * aangegeven of de fout daar zat zodat het paneel die input rood kan
   * kleuren.
   */
  submitMemoryAnswer(name: string, description: string): HdChannelGuessingMemoryAnswerResult {
    if (!this._isActive() || this._mode() !== 'memory') {
      return { kind: 'ignored' };
    }

    const currentKey = this.currentChannelKey();
    if (currentKey == null) {
      return { kind: 'ignored' };
    }

    const translation = this.channelTranslationForGame(currentKey);
    if (translation == null) {
      return { kind: 'ignored' };
    }

    const validations = this._currentMemoryValidations();
    const nameWrong =
      !validations.name &&
      !matchesTargetOrAlternatives(name, translation.name, translation.nameAlternatives);
    const descriptionWrong =
      !validations.description &&
      !matchesTargetOrAlternatives(
        description,
        translation.shortDescription,
        translation.shortDescriptionAlternatives,
      );

    if (!nameWrong && !descriptionWrong) {
      this.handleCorrectGuess(currentKey);
      return { kind: 'correct' };
    }

    this.handleWrongGuess(currentKey);
    return { kind: 'wrong', nameWrong, descriptionWrong };
  }

  /**
   * Valideert losstaand één van beide memory-velden. Een succesvolle
   * validatie kost geen punten, bevriest het betreffende veld op zijn
   * canonieke spelling en blokkeert verdere losse validaties van het andere
   * veld (de speler moet dat dan via `submitMemoryAnswer` afsluiten). Een
   * mislukte validatie heeft géén effect (geen score-impact, geen
   * fout-event in de geschiedenis): het paneel kleurt het veld lokaal rood
   * tot de speler verder typt.
   */
  validateMemoryField(
    field: HdChannelGuessingMemoryField,
    typed: string,
  ): HdChannelGuessingMemoryValidationResult {
    if (!this.canValidateMemoryField(field)) {
      return { kind: 'ignored' };
    }

    const currentKey = this.currentChannelKey();
    if (currentKey == null) {
      return { kind: 'ignored' };
    }

    const translation = this.channelTranslationForGame(currentKey);
    if (translation == null) {
      return { kind: 'ignored' };
    }

    const target = field === 'name' ? translation.name : translation.shortDescription;
    const alternatives =
      field === 'name' ? translation.nameAlternatives : translation.shortDescriptionAlternatives;
    if (!matchesTargetOrAlternatives(typed, target, alternatives)) {
      return { kind: 'wrong' };
    }

    this._currentMemoryValidations.update(v => ({ ...v, [field]: true }));
    return { kind: 'correct', canonical: target };
  }

  /**
   * Geeft de canonieke spelling van de gevraagde veld-waarde van het huidige
   * kanaal terug als autocomplete-suggestie wanneer **een van** de volgende
   * criteria voldaan is op de filler-vrije, genormaliseerde vormen:
   *   1. minstens `MEMORY_AUTOCOMPLETE_MIN_MATCHES` woorden van lengte
   *      `>= MEMORY_AUTOCOMPLETE_MIN_WORD_LENGTH` komen (in willekeurige
   *      volgorde) voor in een variant van het doelwit;
   *   2. de getypte tekst zit op `>= MEMORY_AUTOCOMPLETE_MIN_SIMILARITY`
   *      van een variant van het doelwit (Levenshtein-similariteit).
   * Varianten omvatten o.a. de vorm zonder inhoud-tussen-haakjes, zodat een
   * naam als "Mutatie (Pulse)" ook gesuggereerd wordt wanneer de speler enkel
   * "Mutatie" intypt. Geeft `null` wanneer er geen actief memory-spel is,
   * geen huidig kanaal, geen van beide criteria voldaan is, of de getypte
   * tekst al exact overeenkomt met de canonieke spelling.
   */
  getMemoryAutocompleteSuggestion(field: HdChannelGuessingMemoryField, typed: string): string | null {
    if (!this._isActive() || this._mode() !== 'memory') {
      return null;
    }

    const currentKey = this.currentChannelKey();
    if (currentKey == null) {
      return null;
    }

    const translation = this.channelTranslationForGame(currentKey);
    if (translation == null) {
      return null;
    }

    const target = field === 'name' ? translation.name : translation.shortDescription;
    const alternatives =
      field === 'name' ? translation.nameAlternatives : translation.shortDescriptionAlternatives;
    if (!target) {
      return null;
    }

    if (normalizeMemoryInput(typed) === normalizeMemoryInput(target)) {
      return null;
    }

    if (matchesByWordOverlap(typed, target) || matchesBySimilarity(typed, target)) {
      return target;
    }
    for (const alt of alternatives) {
      if (alt === '') {
        continue;
      }
      if (matchesByWordOverlap(typed, alt) || matchesBySimilarity(typed, alt)) {
        return target;
      }
    }
    return null;
  }

  requestHint(): void {
    if (!this.canRequestHint() || this._mode() === 'memory') {
      return;
    }

    const currentKey = this.currentChannelKey()!;
    this._hintsUsedForCurrentChannel.update(n => n + 1);
    this._hintsUsedTotal.update(n => n + 1);
    this._score.update(s => s - HINT_COST);
    this.persistChannelProgress(currentKey);
    this.appendEvent({ kind: 'hint', channelKey: currentKey, points: -HINT_COST });
  }

  /**
   * Memory-modus hint: onthult `MEMORY_HINT_LETTERS_PER_REQUEST` letters
   * verdeeld over de twee invoervelden. Velden die reeds gevalideerd zijn
   * of waarvoor de getypte tekst al fuzzy-correct is, komen niet in
   * aanmerking voor onthulling. Wanneer geen enkele letter onthuld kan
   * worden (bv. beide velden gevalideerd of geen vooruitgang mogelijk),
   * wordt `null` teruggegeven en is er géén score-impact. Anders worden
   * score (`-HINT_COST`) en hint-tellers bijgewerkt; er wordt bewust
   * **geen** `hint`-event aan de geschiedenis toegevoegd zodat hints in
   * deze modus niet de geschiedenislijst vervuilen.
   */
  requestMemoryHint(
    typedName: string,
    typedDescription: string,
  ): HdChannelGuessingMemoryHintReveal | null {
    if (!this.canRequestHint() || this._mode() !== 'memory') {
      return null;
    }

    const currentKey = this.currentChannelKey();
    if (currentKey == null) {
      return null;
    }

    const translation = this.channelTranslationForGame(currentKey);
    if (translation == null) {
      return null;
    }

    const validations = this._currentMemoryValidations();
    let nameValue = validations.name ? translation.name : typedName;
    let descriptionValue = validations.description ? translation.shortDescription : typedDescription;

    const slots = computeMemoryHintSlots(
      validations,
      typedName,
      typedDescription,
      translation.name,
      translation.nameAlternatives,
      translation.shortDescription,
      translation.shortDescriptionAlternatives,
    );
    if (slots.length === 0) {
      return null;
    }

    let revealed = 0;
    for (const field of slots) {
      const target = field === 'name' ? translation.name : translation.shortDescription;
      const current = field === 'name' ? nameValue : descriptionValue;
      const next = revealNextChar(current, target);
      if (next == null) {
        continue;
      }
      if (field === 'name') {
        nameValue = next;
      } else {
        descriptionValue = next;
      }
      revealed++;
    }

    if (revealed === 0) {
      return null;
    }

    this._hintsUsedForCurrentChannel.update(n => n + 1);
    this._hintsUsedTotal.update(n => n + 1);
    this._score.update(s => s - HINT_COST);
    this.persistChannelProgress(currentKey);
    this.appendEvent({ kind: 'hint', channelKey: currentKey, points: -HINT_COST });
    return { name: nameValue, description: descriptionValue };
  }

  skip(): void {
    if (!this.canSkip()) {
      return;
    }

    const currentKey = this.currentChannelKey()!;
    this.persistChannelProgress(currentKey);
    this._skipsTotal.update(n => n + 1);
    this._score.update(s => s - SKIP_COST);
    this._streak.set(0);
    this._selectedGate.set(null);
    this.appendEvent({ kind: 'skip', channelKey: currentKey, points: -SKIP_COST });
    this._queue.update(queue => [...queue.slice(1), currentKey]);
    this.loadChannelProgress(this.currentChannelKey());
  }

  private evaluateSecondSelection(
    currentKey: string,
    firstGate: number,
    secondGate: number,
  ): HdChannelSelectionResult {
    const candidate = findHdBodygraphChannelByGates(firstGate, secondGate);
    if (candidate && candidate.key === currentKey) {
      this.handleCorrectGuess(currentKey);
      return 'correct';
    }
    this.handleWrongGuess(currentKey);
    return 'wrong';
  }

  private handleCorrectGuess(currentKey: string): void {
    this._score.update(s => s + CORRECT_POINTS);
    this._correctChannelsTotal.update(n => n + 1);
    this._selectedGate.set(null);
    this.appendEvent({ kind: 'correct', channelKey: currentKey, points: CORRECT_POINTS });

    const newStreak = this._streak() + 1;
    this._streak.set(newStreak);

    if (newStreak % STREAK_BONUS_INTERVAL === 0) {
      this._score.update(s => s + STREAK_BONUS_POINTS);
      this.appendEvent({ kind: 'streak-bonus', streak: newStreak, points: STREAK_BONUS_POINTS });
    }

    this._channelProgress.delete(currentKey);
    this._queue.update(queue => queue.slice(1));
    this.loadChannelProgress(this.currentChannelKey());

    if (this.isPerfectGame()) {
      this._score.update(s => s + PERFECT_GAME_BONUS);
      this.appendEvent({ kind: 'perfect-bonus', points: PERFECT_GAME_BONUS });
    }
  }

  private handleWrongGuess(currentKey: string): void {
    this._wrongAttemptsForCurrentChannel.update(n => n + 1);
    this._wrongAttemptsTotal.update(n => n + 1);
    this._score.update(s => s + WRONG_POINTS);
    this._streak.set(0);
    this._selectedGate.set(null);
    this.persistChannelProgress(currentKey);
    this.appendEvent({ kind: 'wrong', channelKey: currentKey, points: WRONG_POINTS });
  }

  private persistChannelProgress(channelKey: string): void {
    this._channelProgress.set(channelKey, {
      wrongAttempts: this._wrongAttemptsForCurrentChannel(),
      hintsUsed: this._hintsUsedForCurrentChannel(),
    });
  }

  private loadChannelProgress(channelKey: string | null): void {
    this._currentMemoryValidations.set({ name: false, description: false });

    if (channelKey == null) {
      this._wrongAttemptsForCurrentChannel.set(0);
      this._hintsUsedForCurrentChannel.set(0);
      return;
    }

    const progress = this._channelProgress.get(channelKey);
    this._wrongAttemptsForCurrentChannel.set(progress?.wrongAttempts ?? 0);
    this._hintsUsedForCurrentChannel.set(progress?.hintsUsed ?? 0);
  }

  private computeScoreLevel(score: number): HdChannelGuessingScoreLevel {
    if (this.isPerfectGame()) {
      return 5;
    }

    const ratio = score / MAX_POSSIBLE_SCORE;
    if (ratio >= 0.85) return 4;
    if (ratio >= 0.65) return 3;
    if (ratio >= 0.4) return 2;
    return 1;
  }

  private appendEvent(event: HdChannelGuessingHistoryEvent): void {
    this._events.update(events => [event, ...events]);
  }

  /**
   * Centrale lookup voor channel-translations tijdens een actief spel: gebruikt
   * de `*In(lang)`-variant met de bevroren {@link _currentLang} zodat alle
   * memory-validaties consistent blijven met de taal die op `start()` actief was.
   */
  private channelTranslationForGame(channelKey: string) {
    return this.gateInfoService.channelTranslationIn(channelKey, this._currentLang());
  }

  private resetState(): void {
    this._isActive.set(false);
    this._mode.set('numbers');
    this._perChannelQuestionMode.set(new Map());
    this._queue.set([]);
    this._events.set([]);
    this._score.set(0);
    this._streak.set(0);
    this._selectedGate.set(null);
    this._wrongAttemptsForCurrentChannel.set(0);
    this._hintsUsedForCurrentChannel.set(0);
    this._hintsUsedTotal.set(0);
    this._wrongAttemptsTotal.set(0);
    this._skipsTotal.set(0);
    this._correctChannelsTotal.set(0);
    this._channelProgress.clear();
    this._currentMemoryValidations.set({ name: false, description: false });
  }
}

function shuffleChannelKeysFisherYates(keys: readonly string[]): string[] {
  const result = [...keys];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Bouwt per-kanaal de concrete vraagweergave. Voor `numbers`/`keynotes` is
 * dit één en dezelfde modus voor alle kanalen. Voor `mixed` wordt per
 * kanaal onafhankelijk een 50/50 keuze gemaakt via `randomFn` (injecteerbaar
 * voor deterministische tests). Voor `memory` blijft de map leeg: die modus
 * gebruikt geen klassieke vraagweergave maar tekstvelden.
 */
function buildPerChannelQuestionMode(
  keys: readonly string[],
  mode: HdChannelGuessingMode,
  randomFn: () => number,
): ReadonlyMap<string, HdChannelGuessingQuestionMode> {
  const result = new Map<string, HdChannelGuessingQuestionMode>();
  if (mode === 'memory') {
    return result;
  }
  for (const key of keys) {
    result.set(key, resolveQuestionMode(mode, randomFn));
  }
  return result;
}

function resolveQuestionMode(
  mode: Exclude<HdChannelGuessingMode, 'memory'>,
  randomFn: () => number,
): HdChannelGuessingQuestionMode {
  if (mode === 'mixed') {
    return randomFn() < 0.5 ? 'numbers' : 'keynotes';
  }
  return mode;
}

/**
 * Bepaalt de hint-slots voor de huidige memory-vraag: welke velden krijgen
 * letters bij dit hint-verzoek? Gevalideerde velden en velden waarvan de
 * getypte tekst reeds fuzzy-correct is, worden uitgesloten. Per hint gaan
 * alle `MEMORY_HINT_LETTERS_PER_REQUEST` letters naar één veld — naam krijgt
 * voorrang zolang die eligible is, daarna schuiven de letters door naar de
 * omschrijving. Geeft een lege array terug wanneer er niets te onthullen valt.
 */
function computeMemoryHintSlots(
  validations: HdChannelGuessingMemoryValidations,
  typedName: string,
  typedDescription: string,
  targetName: string,
  targetNameAlternatives: readonly string[],
  targetDescription: string,
  targetDescriptionAlternatives: readonly string[],
): readonly HdChannelGuessingMemoryField[] {
  const nameEligible =
    !validations.name &&
    !matchesTargetOrAlternatives(typedName, targetName, targetNameAlternatives);
  const descriptionEligible =
    !validations.description &&
    !matchesTargetOrAlternatives(typedDescription, targetDescription, targetDescriptionAlternatives);

  if (nameEligible) {
    return Array<HdChannelGuessingMemoryField>(MEMORY_HINT_LETTERS_PER_REQUEST).fill('name');
  }
  if (descriptionEligible) {
    return Array<HdChannelGuessingMemoryField>(MEMORY_HINT_LETTERS_PER_REQUEST).fill('description');
  }
  return [];
}

/**
 * `true` zodra de getypte tekst fuzzy-matched met de canonieke `primary`
 * spelling óf met één van de geconfigureerde `alternatives`. Hierdoor mag
 * de speler een synoniem of variantnaam intypen en wordt dat als correct
 * aanvaard — de canonieke spelling blijft de waarde die in de UI wordt
 * getoond (zie `validateMemoryField` en `getMemoryAutocompleteSuggestion`).
 */
function matchesTargetOrAlternatives(
  typed: string,
  primary: string,
  alternatives: readonly string[],
): boolean {
  if (fuzzyMemoryMatch(typed, primary)) {
    return true;
  }
  for (const alt of alternatives) {
    if (alt !== '' && fuzzyMemoryMatch(typed, alt)) {
      return true;
    }
  }
  return false;
}

